import { __decorate } from "tslib";
import { CanvasTexture, Sprite, Vector3 } from "three";
import { Save } from "../../../Core/Save.js";
import { Consts } from "../../../Utils/Consts.js";
import { Binding } from "@ortho-next/three-base";
export class Label extends Sprite {
    set anchor(v) {
        this._anchor.copy(v);
        this.updatePosition();
    }
    get anchor() {
        return this._anchor;
    }
    constructor(name, viewType, objToAnchor, anchor = new Vector3(), params = {}, scaleFactor = 1, applyCalibrationScale = true) {
        super();
        this.isLabel = true;
        this._scaleFactor = 1;
        this._fixScaleFactor = 0.04;
        this.updatePosition = () => {
            this.position.copy(this._anchor);
            this._objToAnchor && this.position.add(this._objToAnchor.position);
        };
        this._params = { ...params }; // clone obj
        this._params['parentEventHandler'] = { _object3D: this }; // hack to make history works
        this.setInitialParameters();
        this.name = name;
        this.interceptedByRaycaster = true;
        this._scaleMultiplier = scaleFactor;
        this._scaleFactor = scaleFactor * this._fixScaleFactor;
        this._canvas = document.createElement("canvas");
        this._context = this._canvas.getContext("2d");
        this._objToAnchor = objToAnchor;
        this._originalAnchor = anchor;
        this.defaultAnchor = anchor.clone();
        this._anchor = anchor.clone();
        this.updateCanvas();
        this.material.map = new CanvasTexture(this._canvas);
        this.updatePosition();
        this.updateScale();
        if (applyCalibrationScale) {
            const value = Consts.scaleFactorLabel;
            this.scaleFactor = value;
            this._anchor.copy(this._originalAnchor).multiplyScalar(value);
            this.defaultAnchor.copy(this._originalAnchor).multiplyScalar(value);
            this.updatePosition();
        }
        objToAnchor?.bindEvent("onPositionComponentChange", this.updatePosition);
        this.addEventListener('historyChanged', () => {
            this.updateTexture();
        });
        this.bindProperties();
    }
    bindProperties() {
        Binding.createCustom(`${this.id}_label_visibility`, (m) => m.layer_labels, (value) => {
            // we cannot bind .visible because we set the visibility manually sometimes and this should be refactored.
            this.material.visible = value;
            this.interceptedByRaycaster = value;
            this.needsRender = true;
        }, ['layer_labels']);
    }
    set scaleFactor(value) {
        this._scaleFactor = value * this._scaleMultiplier * this._fixScaleFactor;
        this.updateScale();
    }
    getParam(key) {
        return this._params[key];
    }
    /**
     * Set a new parameter. Returns true if the parameter is different from old one.
     */
    setParam(key, value, updateTexture = true) {
        if (key === "text") {
            const textParams = this.getTextParameters(value);
            if (!this.isTextEquals(textParams)) {
                this._params.text = textParams;
                updateTexture && this.updateTexture();
                return true;
            }
        }
        else if (this._params[key] !== value) {
            this._params[key] = value;
            updateTexture && this.updateTexture();
            return true;
        }
        return false;
    }
    setAllParams(params) {
        let needUpdate = false;
        for (const key in params) {
            needUpdate = this.setParam(key, params[key], false) || needUpdate;
        }
        needUpdate && this.updateTexture();
    }
    isTextEquals(texts) {
        if (this._params.text.length !== texts.length) {
            return false;
        }
        for (let i = 0; i < texts.length; i++) {
            const oldText = this._params.text[i];
            const newText = texts[i];
            for (const key in newText) {
                if (newText[key] !== undefined && oldText[key] !== newText[key]) {
                    return false;
                }
            }
        }
        return true;
    }
    setInitialParameters() {
        const params = this._params;
        params.borderThickness = params.borderThickness ?? 10;
        params.borderColor = params.borderColor || "#fff";
        params.backgroundColor = params.backgroundColor || "#000";
        params.fontFace = params.fontFace || '"Titillium Web", sans-serif';
        params.fontSize = params.fontSize || 72;
        params.fontColor = params.fontColor || "#fff";
        params.text = this.getTextParameters(params.text);
        params.imageBackgroundAlpha = params.imageBackgroundAlpha || 1;
        params.imageBottomRightSize = params.imageBottomRightSize ?? 32;
    }
    getTextParameters(text) {
        const paramText = typeof text === 'object' ? text : [];
        if (typeof text !== 'object') {
            paramText.push({
                text: text,
                fontSize: this._params.fontSize,
                fontColor: this._params.fontColor,
                fontFace: this._params.fontFace
            });
        }
        for (let i = 0; i < paramText.length; i++) {
            const newText = paramText[i];
            const oldText = typeof this._params.text == "object" ? this._params.text[i] : undefined;
            newText.text = newText.text ?? oldText?.text ?? '';
            newText.fontFace = newText.fontFace || oldText?.fontFace || this._params.fontFace;
            newText.fontSize = newText.fontSize || oldText?.fontSize || this._params.fontSize;
            newText.fontColor = newText.fontColor || oldText?.fontColor || this._params.fontColor;
            newText.paddingTop = newText.paddingTop ?? oldText?.paddingTop ?? 0;
            newText.paddingBottom = newText.paddingBottom ?? oldText?.paddingBottom ?? 0;
            newText.paddingLeft = newText.paddingLeft ?? oldText?.paddingLeft ?? 0;
            newText.paddingRight = newText.paddingRight ?? oldText?.paddingRight ?? 0;
            newText['parentEventHandler'] = this._params['parentEventHandler']; // hack to make history works
        }
        ;
        return paramText;
    }
    updateCanvas() {
        const borderThickness = this._params.borderThickness;
        let textWidth = this._params.fixedWidth ?? 0;
        let textHeight = this._params.fixedWidth ?? (borderThickness * 2);
        let maxFontSize = 0;
        for (const param of this._params.text) {
            if (!this._params.fixedWidth) {
                this._context.font = "Bold " + param.fontSize + "px " + param.fontFace; // TODO: add bold to parameters
                const metrics = this._context.measureText(param.text);
                textHeight += metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent + param.paddingTop + param.paddingBottom;
                const currentWidth = metrics.width + param.paddingLeft + param.paddingRight;
                textWidth = Math.max(currentWidth, textWidth);
            }
            maxFontSize = Math.max(param.fontSize, maxFontSize);
        }
        ;
        this._canvas.width = textWidth + borderThickness * 4;
        this._canvas.height = textHeight + borderThickness * 2;
        this._context.clearRect(0, 0, this._canvas.width, this._canvas.height);
        this.drawRoundedRect(maxFontSize, borderThickness);
        this.drawBackgroundImage(borderThickness);
        this.drawText();
        this.drawBottomRightImage(borderThickness);
    }
    drawRoundedRect(fontSize, borderThickness) {
        if (borderThickness > 0) {
            const x = borderThickness / 2;
            const y = borderThickness / 2;
            const width = this._canvas.width - borderThickness;
            const height = this._canvas.height - borderThickness;
            const radius = fontSize / 3;
            this._context.fillStyle = this._params.backgroundColor;
            this._context.strokeStyle = this._params.borderColor;
            this._context.lineWidth = borderThickness;
            this._context.beginPath();
            this._context.moveTo(x + radius, y);
            this._context.lineTo(x + width - radius, y);
            this._context.quadraticCurveTo(x + width, y, x + width, y + radius);
            this._context.lineTo(x + width, y + height - radius);
            this._context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
            this._context.lineTo(x + radius, y + height);
            this._context.quadraticCurveTo(x, y + height, x, y + height - radius);
            this._context.lineTo(x, y + radius);
            this._context.quadraticCurveTo(x, y, x + radius, y);
            this._context.closePath();
            this._context.fill();
            this._context.stroke();
        }
    }
    drawBackgroundImage(borderThickness) {
        if (this._params.imageBackground) {
            const img = this._params.imageBackground;
            const aspectRatioImg = img.width / img.height;
            const aspectRatioCanvas = this._canvas.width / this._canvas.height;
            if (aspectRatioImg > aspectRatioCanvas) {
                const imageHeight = ((this._canvas.width - borderThickness * 2) * img.height) / img.width;
                this._context.globalAlpha = this._params.imageBackgroundAlpha;
                this._context.drawImage(img, borderThickness, this._canvas.height / 2 - imageHeight / 2, this._canvas.width - borderThickness * 2, imageHeight);
            }
            else {
                const imageWidth = ((this._canvas.height - borderThickness * 2) * img.width) / img.height;
                this._context.globalAlpha = this._params.imageBackgroundAlpha;
                this._context.drawImage(img, this._canvas.width / 2 - imageWidth / 2, borderThickness, imageWidth, this._canvas.height - borderThickness * 2);
            }
            this._context.globalAlpha = 1;
        }
    }
    drawText() {
        let y = 0;
        let prevPaddingBottom = 0;
        for (const text of this._params.text) {
            const fontSize = text.fontSize;
            this._context.font = "Bold " + fontSize + "px " + text.fontFace; // This is import to repeat after canvas resize
            this._context.fillStyle = text.fontColor;
            y += fontSize + text.paddingTop + prevPaddingBottom;
            prevPaddingBottom = text.paddingBottom;
            const x = this._params.borderThickness * 2 + text.paddingLeft;
            this._context.fillText(text.text, x, y);
        }
    }
    drawBottomRightImage(borderThickness) {
        if (this._params.imageBottomRight) {
            const img = this._params.imageBottomRight;
            const padding = borderThickness;
            const size = this._params.imageBottomRightSize;
            // Calculate size maintaining aspect ratio
            const aspectRatio = img.width / img.height;
            let imageWidth = size;
            let imageHeight = size;
            if (aspectRatio > 1) {
                imageHeight = size / aspectRatio;
            }
            else {
                imageWidth = size * aspectRatio;
            }
            // Position in bottom right corner with padding
            const x = this._canvas.width - imageWidth - padding;
            const y = this._canvas.height - imageHeight - padding;
            this._context.drawImage(img, x, y, imageWidth, imageHeight);
        }
    }
    updateScale() {
        this.scale.set(this._canvas.width * this._scaleFactor, this._canvas.height * this._scaleFactor, 1);
    }
    resetPosition() {
        this._anchor.copy(this.defaultAnchor);
        this.updatePosition();
    }
    dispose() {
        super.dispose();
        this.material.map.dispose();
        this.material.dispose();
        this.geometry.dispose();
    }
    updateTexture() {
        const oldWidth = this._canvas.width;
        const oldHeight = this._canvas.height;
        this.updateCanvas();
        if (this._canvas.width === oldWidth && this._canvas.height === oldHeight) {
            this.material.map.needsUpdate = true;
        }
        else {
            this.material.map.dispose();
            const texture = new CanvasTexture(this._canvas); //if texture sizes changes, it's necessary recreate a CanvasTexture
            this.material.map = texture;
            this.updateScale();
        }
        this.needsRender = true;
    }
}
__decorate([
    Save()
], Label.prototype, "_anchor", void 0);
//# sourceMappingURL=Label.js.map